import { useEffect, useState } from 'react';
import { useLocale, type Option } from 'shared-ui';
import { FormProvider, useForm } from 'react-hook-form';

import { useModalState } from 'hooks/useModalState/useModalState';
import { useQuery } from 'hooks/useQuery/useQuery';
import { QueryKeyEnum } from 'core/global.enum';
import { AppMessages } from 'i18n/messages';
import { downloadPresentOwnersReport, fetchPresentOwnersReportZones } from 'api/reports/reports';
import { STREFA_ROOT_ID } from 'api/strefy/strefy.enum';
import { useReportDownload } from '../hooks/useReportDownload';
import { useSmtpConfiguration } from '../hooks/useSmtpConfiguration';
import { EmailSelectionModalContainer } from '../emailSelectionModal';

import { PresentOwnersReportForm } from './PresentOwnersReport.types';
import { PresentOwnersReport } from './PresentOwnersReport';

export const PresentOwnersReportContainer = () => {
  const { isModalOpen, onModalClose, onModalOpen } = useModalState();
  const {
    isModalOpen: isEmailModalOpen,
    onModalClose: onEmailModalClose,
    onModalOpen: onEmailModalOpen,
  } = useModalState();
  const { formatMessage } = useLocale();
  const [selectedEmails, setSelectedEmails] = useState<Option[]>([]);
  const { isSmtpConfigured } = useSmtpConfiguration();

  const { data: zonesResponse } = useQuery([QueryKeyEnum.PRESENT_OWNERS_REPORT_STREFY], fetchPresentOwnersReportZones, {
    enabled: isModalOpen,
  });

  // No zone selected already means the root zone (contract §6), so the root itself is not offered.
  const zones = (zonesResponse?.data || []).flatMap((zone) => (zone.id === STREFA_ROOT_ID ? zone.children : [zone]));

  const formMethods = useForm<PresentOwnersReportForm>({
    mode: 'all',
    defaultValues: { nazwisko: '', nrEwidencyjny: '' },
  });

  const { nazwisko, nrEwidencyjny, komorka, strefa } = formMethods.watch();

  const {
    onDownload: onDownloadPresentOwnersReport,
    isLoading: isLoadingDownload,
    hasNoDataError,
    hasNetworkError,
    resetError,
  } = useReportDownload({
    mutationFn: downloadPresentOwnersReport,
    buildRequestBody: (emails) => ({
      // Empty criteria are left out — the backend treats a missing field as "no filter".
      nazwisko: nazwisko?.trim() || undefined,
      nrEwidencyjny: nrEwidencyjny?.trim() || undefined,
      komorka: komorka?.id ? [komorka.id] : [],
      strefaId: strefa?.id,
      selectedEmails: emails,
    }),
    onEmailModalClose,
    reportNameId: AppMessages['reports.presentOwnersReport.title'],
  });

  const handleEmailModalOpen = () => {
    resetError();
    onEmailModalOpen();
  };

  const handleConfirmEmails = (emails: string[]) => {
    setSelectedEmails(emails.map((email) => ({ value: email, label: email })));
    onDownloadPresentOwnersReport(emails);
  };

  useEffect(() => {
    if (!isModalOpen) {
      formMethods.reset();
      resetError();
      setSelectedEmails([]);
    }
  }, [isModalOpen]);

  return (
    <FormProvider {...formMethods}>
      <PresentOwnersReport
        zones={zones}
        isModalOpen={isModalOpen}
        onModalClose={onModalClose}
        onModalOpen={onModalOpen}
        onDownload={onDownloadPresentOwnersReport}
        isLoadingDownload={isLoadingDownload}
        hasNoDataError={hasNoDataError}
        hasNetworkError={hasNetworkError}
        onEmailModalOpen={handleEmailModalOpen}
        isEmailModalOpen={isEmailModalOpen}
        isSmtpConfigured={isSmtpConfigured}
        onResetError={resetError}
      />
      {isModalOpen && (
        <EmailSelectionModalContainer
          title={formatMessage({ id: AppMessages['reports.presentOwnersReport.title'] })}
          open={isEmailModalOpen}
          onClose={onEmailModalClose}
          onConfirm={handleConfirmEmails}
          hasNoDataError={hasNoDataError}
          onResetError={resetError}
          selectedUsers={selectedEmails}
        />
      )}
    </FormProvider>
  );
};
